import { useCallback, useEffect, useRef, useState } from "react";
import type { Hotspot } from "../../shared/types";
import { newId } from "../api";

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
export const round = (v: number) => Math.round(v * 100) / 100;
// Roughly 44px on a typical ~1400px-wide screen: small enough not to get in the way,
// large enough that a drawn hotspot isn't an unreachable sliver on the live demo.
export const MIN_HOTSPOT_PCT = 3;

type Drag =
  | { kind: "draw"; x0: number; y0: number; x: number; y: number }
  | { kind: "move"; id: string; dx: number; dy: number }
  | { kind: "resize"; id: string };

const newHotspot = (x: number, y: number, w: number, h: number): Hotspot => ({
  id: newId(), x: round(x), y: round(y), w: round(w), h: round(h),
  action: "advance_next", targetStepId: null, tooltipText: "", linkUrl: "", pulse: true,
});

/**
 * Drawing, moving, resizing and selecting hotspots on a screen, with undo. Shared by the
 * Hotspots step and the full-screen page editor so both behave the same.
 * `keys` turns on keyboard shortcuts (Delete, arrows, Esc, Ctrl/Cmd+Z) while mounted.
 */
export function useHotspotCanvas(initial: Hotspot[], opts: { keys?: boolean; onDirty?: (d: boolean) => void } = {}) {
  const [hs, setHs] = useState<Hotspot[]>(initial);
  const [past, setPast] = useState<Hotspot[][]>([]);
  const [future, setFuture] = useState<Hotspot[][]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const lastPatch = useRef<{ key: string; at: number } | null>(null);
  const onDirty = useRef(opts.onDirty);
  onDirty.current = opts.onDirty;

  const [cleanHs, setCleanHs] = useState<Hotspot[]>(initial);
  const dirty = JSON.stringify(hs) !== JSON.stringify(cleanHs);
  useEffect(() => { onDirty.current?.(dirty); }, [dirty]);

  const markClean = useCallback((currentHs?: Hotspot[]) => {
    setCleanHs(currentHs ?? hs);
  }, [hs]);

  /** Replace everything (new screen, or after discarding): clears undo/redo history. */
  const reset = useCallback((next: Hotspot[]) => {
    setHs(next); setPast([]); setFuture([]); setSel(null); setDrag(null); lastPatch.current = null;
    setCleanHs(next);
  }, []);

  /** A change that can be undone. Any new change drops the redo history. */
  const commit = (next: Hotspot[]) => {
    setPast((p) => [...p, hs].slice(-100));
    setFuture([]);
    setHs(next);
    lastPatch.current = null;
  };
  const undo = () => {
    if (!past.length) return;
    setFuture((f) => [...f, hs].slice(-100));
    setHs(past[past.length - 1]);
    setPast((p) => p.slice(0, -1));
    setSel(null);
    lastPatch.current = null;
  };
  const redo = () => {
    if (!future.length) return;
    setPast((p) => [...p, hs].slice(-100));
    setHs(future[future.length - 1]);
    setFuture((f) => f.slice(0, -1));
    setSel(null);
    lastPatch.current = null;
  };

  const toPct = (e: { clientX: number; clientY: number }) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: clamp(((e.clientX - r.left) / r.width) * 100, 0, 100), y: clamp(((e.clientY - r.top) / r.height) * 100, 0, 100) };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const p = toPct(e);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setSel(null);
    setDrag({ kind: "draw", x0: p.x, y0: p.y, x: p.x, y: p.y });
  };
  const onBoxDown = (e: React.PointerEvent, h: Hotspot, resize: boolean) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    canvasRef.current!.setPointerCapture(e.pointerId);
    setSel(h.id);
    const p = toPct(e);
    setPast((ps) => [...ps, hs].slice(-100)); // one undo step per drag
    setFuture([]);
    lastPatch.current = null;
    setDrag(resize ? { kind: "resize", id: h.id } : { kind: "move", id: h.id, dx: p.x - h.x, dy: p.y - h.y });
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const p = toPct(e);
    if (drag.kind === "draw") setDrag({ ...drag, x: p.x, y: p.y });
    else if (drag.kind === "move") {
      setHs(hs.map((h) => (h.id === drag.id ? { ...h, x: round(clamp(p.x - drag.dx, 0, 100 - h.w)), y: round(clamp(p.y - drag.dy, 0, 100 - h.h)) } : h)));
    } else {
      setHs(hs.map((h) => (h.id === drag.id ? { ...h, w: round(clamp(p.x - h.x, MIN_HOTSPOT_PCT, 100 - h.x)), h: round(clamp(p.y - h.y, MIN_HOTSPOT_PCT, 100 - h.y)) } : h)));
    }
  };
  const onPointerUp = () => {
    if (drag?.kind === "draw") {
      const x = Math.min(drag.x0, drag.x), y = Math.min(drag.y0, drag.y);
      const w = Math.abs(drag.x - drag.x0), h = Math.abs(drag.y - drag.y0);
      if (w >= MIN_HOTSPOT_PCT && h >= MIN_HOTSPOT_PCT) {
        const n = newHotspot(x, y, w, h);
        commit([...hs, n]);
        setSel(n.id);
      }
    }
    setDrag(null);
  };
  const onPointerCancel = () => setDrag(null);

  /** Keyboard path: a default box in the middle, placed with arrows or the number fields. */
  const addCentered = () => {
    const n = newHotspot(40, 42.5, 20, 15);
    commit([...hs, n]);
    setSel(n.id);
  };

  /**
   * Slide hotspots by a per-id offset (in %), as the page under them scrolls. Not an edit: no undo
   * step, no dirty flag. The undo/redo snapshots slide too, so undoing later restores a box to
   * where its page content is now, not to where it was on screen back then.
   */
  const shift = (by: Map<string, { dx: number; dy: number }>) => {
    const move = (list: Hotspot[]) => list.map((h) => {
      const s = by.get(h.id);
      return s ? { ...h, x: round(h.x + s.dx), y: round(h.y + s.dy) } : h;
    });
    setHs(move);
    setPast((p) => p.map(move));
    setFuture((f) => f.map(move));
    setCleanHs(move);
  };

  const selected = hs.find((h) => h.id === sel) ?? null;
  /** Typing in one field is one undo step, not one per keystroke. */
  const patchSel = (p: Partial<Hotspot>) => {
    const key = `${sel}:${Object.keys(p).sort().join(",")}`;
    const next = hs.map((h) => (h.id === sel ? { ...h, ...p } : h));
    const recent = lastPatch.current && lastPatch.current.key === key && Date.now() - lastPatch.current.at < 1500;
    if (recent) setHs(next); else { setPast((ps) => [...ps, hs].slice(-100)); setFuture([]); setHs(next); }
    lastPatch.current = { key, at: Date.now() };
  };
  const removeSel = () => { commit(hs.filter((h) => h.id !== sel)); setSel(null); };

  useEffect(() => {
    if (!opts.keys) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !typing) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      if (!sel || typing) return;
      const d = e.shiftKey ? 2 : 0.5;
      const nudge: Record<string, [number, number]> = { ArrowLeft: [-d, 0], ArrowRight: [d, 0], ArrowUp: [0, -d], ArrowDown: [0, d] };
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeSel(); }
      else if (nudge[e.key]) {
        e.preventDefault();
        const [dx, dy] = nudge[e.key];
        commit(hs.map((h) => (h.id === sel ? { ...h, x: round(clamp(h.x + dx, 0, 100 - h.w)), y: round(clamp(h.y + dy, 0, 100 - h.h)) } : h)));
      } else if (e.key === "Escape") setSel(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const draft = drag?.kind === "draw"
    ? { x: Math.min(drag.x0, drag.x), y: Math.min(drag.y0, drag.y), w: Math.abs(drag.x - drag.x0), h: Math.abs(drag.y - drag.y0) }
    : null;

  return {
    hs, sel, setSel, selected, dirty, canUndo: past.length > 0, canRedo: future.length > 0, depth: past.length,
    undo, redo, reset, markClean, commit, patchSel, removeSel, addCentered, shift,
    canvasRef, draft, onBoxDown,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
  };
}
export type HotspotCanvas = ReturnType<typeof useHotspotCanvas>;

/** The hotspot boxes and the box being drawn. Place inside the element holding `canvasRef`. */
export function HotspotBoxes({ c }: { c: HotspotCanvas }) {
  return (
    <>
      {c.hs.map((h, i) => (
        <div key={h.id} className={`hs-box ${h.id === c.sel ? "sel" : ""}`}
          style={{ left: `${h.x}%`, top: `${h.y}%`, width: `${h.w}%`, height: `${h.h}%` }}
          onPointerDown={(e) => c.onBoxDown(e, h, false)}>
          <span className="lbl">{i + 1}</span>
          {h.id === c.sel && <span className="handle" onPointerDown={(e) => c.onBoxDown(e, h, true)} />}
        </div>
      ))}
      {c.draft && <div className="hs-draft" style={{ left: `${c.draft.x}%`, top: `${c.draft.y}%`, width: `${c.draft.w}%`, height: `${c.draft.h}%` }} />}
    </>
  );
}

/** Round-arrow undo icon button. */
export function UndoButton({ onClick, disabled, label = "Undo", compact = false }: { onClick: () => void; disabled: boolean; label?: string; compact?: boolean }) {
  return (
    <button type="button" className="btn btn-sm" onClick={onClick} disabled={disabled} title={`${label} (Ctrl+Z)`} aria-label={label}>
      <svg className="icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3.5 3.5v3.25h3.25" /><path d="M3.9 6.6A5 5 0 1 1 3.25 9.5" />
      </svg>
      {!compact && label}
    </button>
  );
}

/** Same icon, mirrored: redo. */
export function RedoButton({ onClick, disabled, label = "Redo", compact = false }: { onClick: () => void; disabled: boolean; label?: string; compact?: boolean }) {
  return (
    <button type="button" className="btn btn-sm" onClick={onClick} disabled={disabled} title={`${label} (Ctrl+Shift+Z)`} aria-label={label}>
      <svg className="icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ transform: "scaleX(-1)" }}>
        <path d="M3.5 3.5v3.25h3.25" /><path d="M3.9 6.6A5 5 0 1 1 3.25 9.5" />
      </svg>
      {!compact && label}
    </button>
  );
}
